/**
 * useSessionGuard - Proactive Session Management Hook
 *
 * Monitors session expiration and provides early warnings to prevent data loss.
 *
 * Features:
 * - Real-time session state tracking (valid/expiring/expired)
 * - Countdown timer for session expiration
 * - Automatic session refresh capability
 * - Prevents form submission when session is expired
 *
 * Usage:
 * ```tsx
 * const { sessionState, expiresIn, refreshSession } = useSessionGuard();
 *
 * if (sessionState === 'expiring') {
 *   // Show warning: session expires in expiresIn seconds
 * }
 * ```
 */

import { useEffect, useState, useCallback } from 'react';
import { getCurrentSession } from '@/lib/rpc/auth-facade';
import { supabase } from '@/integrations/supabase/client';

export type SessionState = 'valid' | 'expiring' | 'expired' | 'checking';

export interface UseSessionGuardResult {
  sessionState: SessionState;
  expiresIn: number | null; // Seconds until expiration
  refreshSession: () => Promise<void>;
  isSessionValid: boolean;
}

const EXPIRY_WARNING_THRESHOLD = 5 * 60; // 5 minutes in seconds
const CHECK_INTERVAL = 30 * 1000; // 30 seconds

export function useSessionGuard(): UseSessionGuardResult {
  const [sessionState, setSessionState] = useState<SessionState>('checking');
  const [expiresIn, setExpiresIn] = useState<number | null>(null);

  const checkSession = useCallback(async () => {
    try {
      const session = await getCurrentSession();

      if (!session?.access_token) {
        setSessionState('expired');
        setExpiresIn(null);
        return;
      }

      const expiresAt = session.expires_at ? new Date(session.expires_at * 1000) : null;
      if (!expiresAt) {
        // No expiration info → assume valid
        setSessionState('valid');
        setExpiresIn(null);
        return;
      }

      const now = Date.now();
      const timeLeftMs = expiresAt.getTime() - now;
      const timeLeftSec = Math.floor(timeLeftMs / 1000);

      if (timeLeftSec <= 0) {
        setSessionState('expired');
        setExpiresIn(0);
      } else if (timeLeftSec <= EXPIRY_WARNING_THRESHOLD) {
        setSessionState('expiring');
        setExpiresIn(timeLeftSec);
      } else {
        setSessionState('valid');
        setExpiresIn(timeLeftSec);
      }
    } catch (error) {
      console.error('[useSessionGuard] Failed to check session:', error);
      setSessionState('expired');
      setExpiresIn(null);
    }
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();

      if (error) {
        console.error('[useSessionGuard] Failed to refresh session:', error);
        setSessionState('expired');
        return;
      }

      if (data.session) {
        console.log('[useSessionGuard] Session refreshed successfully');
        // Re-check session to update state
        await checkSession();
      } else {
        setSessionState('expired');
      }
    } catch (error) {
      console.error('[useSessionGuard] Refresh session error:', error);
      setSessionState('expired');
    }
  }, [checkSession]);

  // Initial check
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  // Periodic checks
  useEffect(() => {
    const interval = setInterval(checkSession, CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, [checkSession]);

  // Countdown timer (updates every second when expiring)
  useEffect(() => {
    if (sessionState !== 'expiring' || expiresIn === null) {
      return;
    }

    const timer = setInterval(() => {
      setExpiresIn(prev => {
        if (prev === null || prev <= 0) {
          setSessionState('expired');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [sessionState, expiresIn]);

  return {
    sessionState,
    expiresIn,
    refreshSession,
    isSessionValid: sessionState === 'valid' || sessionState === 'expiring',
  };
}
